import React, { useState } from "react";
import styled from "styled-components";
import { Icon, IconButton } from "@material-ui/core";

import { IWorkspaceUserExtended, IWorkspaceUserGroup, WorkspaceUserRole } from "common/interfaces";
import { auth } from "js/firebase/auth";
import { t } from "js/i18n";
import { UserGroupProfile, UserProfile } from "js/react/components/Avatar";
import { FlexSpacer } from "js/react/components/Gap";
import GuestBadge from "js/react/components/GuestBadge";
import { ScrollBox } from "js/react/components/LayoutGrid";

const AccessSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    align-self: stretch;
    width: 100%;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    gap: 8px;
`;

const SectionLabel = styled.span`
    color: #333;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
`;

const AccessList = styled.div`
    width: 100%;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.10);
`;

const NoMembers = styled.div`
    font-style: italic;
    color: #666;
    padding: 14px;
`;

const MemberRow = styled.div.attrs({ "data-test-id": "team-folder-member-row" })<{ $interactive?: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 10px 20px;

    ${props => props.$interactive && `
        &:hover {
            background: #f7fafc;
        }
    `}

    & + & {
        border-top: 1px solid #eee;
    }
`;

const MemberRowActions = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
`;

const HoverIconButton = styled(IconButton)`
    &&& {
        padding: 0;
        opacity: 0;
        transition: opacity 0.2s ease, color 0.2s ease;
        color: #999;
        background-color: transparent;

        ${MemberRow}:hover & {
            opacity: 1;
        }

        &:hover {
            color: #FF4500;
            background-color: transparent;
        }
    }
`;

function getMemberProfile(member: IWorkspaceUserExtended, appendYouLabel: boolean) {
    if (!appendYouLabel) {
        return member;
    }

    const youSuffix = ` (${t("You")})`;
    if (member.displayName) {
        return { ...member, displayName: `${member.displayName}${youSuffix}` };
    }

    return { ...member, email: `${member.email}${youSuffix}` };
}

function RemoveMemberButton({ onClick, "aria-label": ariaLabel }: { onClick: () => void; "aria-label": string }) {
    const [hovered, setHovered] = useState(false);

    return (
        <HoverIconButton
            onClick={onClick}
            aria-label={ariaLabel}
            disableRipple
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Icon>{hovered ? "remove_circle" : "remove_circle_outline"}</Icon>
        </HoverIconButton>
    );
}

type AccessGroup = IWorkspaceUserGroup & {
    memberCount?: number;
};

export interface FolderAccessListProps {
    members: IWorkspaceUserExtended[];
    groups?: AccessGroup[];
    headerLabel?: string;
    emptyMessage?: string;
    headerActions?: React.ReactNode;
    onRemoveMember?: (uid: string) => void;
    onRemoveGroup?: (groupId: string) => void;
    canRemoveMember?: (member: IWorkspaceUserExtended) => boolean;
    canRemoveGroup?: (group: AccessGroup) => boolean;
    showYouLabel?: boolean;
}

export function FolderAccessList({
    members,
    groups = [],
    headerLabel = t("Who has access?"),
    emptyMessage = t("No team members can access this folder"),
    headerActions,
    onRemoveMember,
    onRemoveGroup,
    canRemoveMember,
    canRemoveGroup,
    showYouLabel = false,
}: FolderAccessListProps) {
    const interactive = !!onRemoveMember || !!onRemoveGroup;
    const currentUserUid = auth().currentUser?.uid;

    return (
        <AccessSection>
            <SectionHeader>
                <SectionLabel>{headerLabel}</SectionLabel>
                {headerActions && (
                    <>
                        <FlexSpacer />
                        {headerActions}
                    </>
                )}
            </SectionHeader>
            <AccessList>
                {/* @ts-ignore */}
                <ScrollBox fillWidth>
                    {groups.map(group => (
                        <MemberRow key={group.id} $interactive={interactive}>
                            <UserGroupProfile profile={group} />
                            <FlexSpacer />
                            <MemberRowActions>
                                {onRemoveGroup && (canRemoveGroup?.(group) ?? true) &&
                                <RemoveMemberButton
                                    onClick={() => onRemoveGroup(group.id)}
                                    aria-label={t("Remove group")}
                                />
                                }
                            </MemberRowActions>
                        </MemberRow>
                    ))}
                    {members.map(member => (
                        <MemberRow key={member.id} $interactive={interactive}>
                            <UserProfile profile={getMemberProfile(member, showYouLabel && member.uid === currentUserUid)} />
                            <FlexSpacer />
                            <MemberRowActions>
                                {member.role === WorkspaceUserRole.GUEST && <GuestBadge />}
                                {onRemoveMember && (canRemoveMember?.(member) ?? (members.length > 1 && member.uid !== auth().currentUser.uid)) &&
                                    <RemoveMemberButton
                                        onClick={() => onRemoveMember(member.uid)}
                                        aria-label={t("Remove member")}
                                    />
                                }
                            </MemberRowActions>
                        </MemberRow>
                    ))}
                    {groups.length === 0 && members.length === 0 && (
                        <NoMembers>{emptyMessage}</NoMembers>
                    )}
                </ScrollBox>
            </AccessList>
        </AccessSection>
    );
}
